import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Box, Typography, Button, Grid, alpha } from "@mui/material";
import { AddCircle, Payment } from "@mui/icons-material";
import {
  CheckCircle,
  Schedule,
  Cancel,
  LocalDining,
  Receipt,
  TaskAlt,
} from "@mui/icons-material";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import OrderServices from "../../../services/OrderService";
import PaymentService from "../../../services/PaymentService";
import OrderHeader from "./OrderDetails/OrderHeader";
import OrderInfoSection from "./OrderDetails/OrderInfoSection";
import OrderItemsSection from "./OrderDetails/OrderItemsSection";
import OrderSummarySection from "./OrderDetails/OrderSummarySection";
import OrderActionsSection from "./OrderDetails/OrderActionsSection";
import StatusConfirmDialog from "./OrderDetails/StatusConfirmDialog";
import OrderCheckoutDialog from "./OrderCheckout/OrderCheckoutDialog";
import CustomerBill from "./OrderCheckout/CustomerBill";
import { useUserWithCompany } from "../../../hooks/useCompany";
import { calculateOrderTotals } from "../utils/cartUtils";

const OrderDetailsScreen = ({
  orderId,
  onBack,
  onPOSClick,
  isFullscreen,
  toggleFullscreen,
  fullscreenLabel,
  onOrderUpdated,
  onEditOrder,
}) => {
  const {
    companyId,
    vatRate,
    serviceChargeRateDining,
    serviceChargeRateTakeaway,
    currency,
    company,
  } = useUserWithCompany();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    status: null,
  });
  const [hasPayments, setHasPayments] = useState(true);
  const [checkingPayments, setCheckingPayments] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showCompletedDialog, setShowCompletedDialog] = useState(false);

  // Fetch order details
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await OrderServices.getOrderById(orderId, {
          companyId,
        });
        if (response?.data?.view) {
          console.log("Order Details API Response:", response.data.view);
          setOrderDetails(response.data.view);
        } else {
          console.error("No order details in response");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId, companyId]);

  // Check if order has payments
  useEffect(() => {
    const checkPayments = async () => {
      if (!orderId || !companyId) return;

      try {
        setCheckingPayments(true);
        const response = await PaymentService.getAll({
          companyId,
          orderId,
          page: 0,
          limit: 10,
        });

        const payments = response?.data?.view?.data || [];
        setHasPayments(payments.length > 0);
      } catch (error) {
        console.error("Error checking payments:", error);
        setHasPayments(false);
      } finally {
        setCheckingPayments(false);
      }
    };

    if (orderDetails) {
      checkPayments();
    }
  }, [orderId, companyId, orderDetails]);

  // Status configuration
  const getStatusConfig = (status) => {
    switch (status) {
      case "completed":
        return {
          color: "#059669",
          label: "Completed",
          icon: CheckCircle,
        };
      case "active":
        return {
          color: "#f59e0b",
          label: "Active",
          icon: LocalDining,
        };
      case "ready":
        return {
          color: "#10b981",
          label: "Ready",
          icon: CheckCircle,
        };
      case "served":
        return {
          color: "#6b7280",
          label: "Served",
          icon: LocalDining,
        };
      case "preparing":
        return {
          color: "#667eea",
          label: "Preparing",
          icon: Schedule,
        };
      case "pending":
        return {
          color: "#f59e0b",
          label: "Pending",
          icon: Schedule,
        };
      case "confirmed":
        return {
          color: "#FF6600",
          label: "Confirmed",
          icon: CheckCircle,
        };
      case "payment-pending":
        return {
          color: "#ef4444",
          label: "Payment Due",
          icon: Receipt,
        };
      case "cancelled":
        return {
          color: "#ef4444",
          label: "Cancelled",
          icon: Cancel,
        };
      default:
        return {
          color: "#6b7280",
          label: status,
          icon: Receipt,
        };
    }
  };

  // Order summary calculations
  const calculateOrderSummary = () => {
    if (!orderDetails) return null;

    // Convert order items to cart format for calculation
    const cart = transformOrderToCart();

    // Get order type from order details
    const orderType = orderDetails.orderType || "takeaway";

    // Recalculate using the same logic as OrderCheckoutDialog
    const calculatedTotals = calculateOrderTotals(cart, orderType, {
      serviceChargeRateDining,
      serviceChargeRateTakeaway,
      vatRate,
    });

    console.log("Recalculated Order Totals:", calculatedTotals);
    console.log("Order Type:", orderType);
    console.log("Company Rates:", {
      serviceChargeRateDining,
      serviceChargeRateTakeaway,
      vatRate,
    });

    // Get discount from order details (if any)
    const discount = parseFloat(orderDetails.discountAmount || 0);

    // Calculate final total with discount
    const finalTotal = Math.max(0, calculatedTotals.grandTotal - discount);

    const summary = {
      subtotal: calculatedTotals.subtotal,
      discount: discount,
      vat: calculatedTotals.vatAmount,
      serviceCharge: calculatedTotals.serviceCharge,
      total: finalTotal,
    };

    console.log("Final Order Summary:", summary);

    return summary;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `Rs. ${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
  };

  // Handle actions
  const handleStatusUpdate = (newStatus) => {
    setConfirmDialog({ open: true, status: newStatus });
  };

  const handleConfirmStatusUpdate = async () => {
    const isTakeaway = orderDetails.orderType === "takeaway";
    const ORDER_FLOW = isTakeaway
      ? ["pending", "confirmed", "preparing", "ready", "completed"]
      : ["pending", "confirmed", "preparing", "ready", "served", "completed"];
    try {
      const targetStatus = confirmDialog.status;
      const currentIdx = ORDER_FLOW.indexOf(orderDetails.status);
      const targetIdx = ORDER_FLOW.indexOf(targetStatus);
      const preparingIdx = ORDER_FLOW.indexOf("preparing");

      // If skipping over "preparing", hit it first before the target status
      if (currentIdx < preparingIdx && targetIdx > preparingIdx) {
        await OrderServices.updateOrderStatusById(orderId, "preparing");
      }

      await OrderServices.updateOrderStatusById(orderId, targetStatus);
      setOrderDetails((prev) => ({ ...prev, status: targetStatus }));
      // Trigger orders refresh
      if (onOrderUpdated) {
        onOrderUpdated();
      }
      // Navigate back to orders screen after successful status update
      setTimeout(() => {
        onBack();
      }, 300);
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, status: null });
  };

  const customerBillRef = useRef();

  const triggerPrint = useReactToPrint({
    contentRef: customerBillRef,
    documentTitle: `Receipt - ${orderDetails?.orderNumber || orderId}`,
  });

  const handlePrint = () => {
    triggerPrint();
  };

  const handleShare = () => {
    console.log("Share order:", orderId);
    // Implement share functionality
  };

  // Transform order items back to cart format for checkout
  const transformOrderToCart = () => {
    if (!orderDetails || !orderDetails.OrderItems) return [];

    return orderDetails.OrderItems.map((item) => {
      if (item.offerId) {
        // Handle offer items
        return {
          id: item.offerId,
          isOffer: true,
          offerId: item.offerId,
          name: item.Offer?.name || "Offer",
          price: parseFloat(item.unitPrice || 0),
          quantity: item.quantity,
          totalPrice: parseFloat(item.totalPrice || 0),
        };
      }

      // Handle regular menu items
      const cartItem = {
        id: item.menuItemId,
        name: item.MenuItem?.name || "Item",
        price: parseFloat(item.unitPrice || 0),
        quantity: item.quantity,
        totalPrice: parseFloat(item.totalPrice || 0),
        image: item.MenuItem?.image,
      };

      // Add variation if exists
      if (item.ItemVariation) {
        cartItem.selectedVariation = {
          id: item.variationId,
          name: item.ItemVariation.name,
          price: parseFloat(item.ItemVariation.price || 0),
        };
      }

      // Add modifiers if exist
      if (item.OrderItemModifiers && item.OrderItemModifiers.length > 0) {
        cartItem.selectedModifiers = item.OrderItemModifiers.map((mod) => ({
          id: mod.modifierOptionId,
          name: mod.ModifierOption?.name || "Modifier",
          price: parseFloat(mod.ModifierOption?.price || 0),
        }));
      }

      return cartItem;
    });
  };

  // Transform pending/active order items to cart format for editing (with orderItemId for PATCH/DELETE)
  const transformPendingItemsToCart = () => {
    if (!orderDetails?.OrderItems) return [];
    const editableStatuses = ["pending", "preparing", "ready", "served"];
    return orderDetails.OrderItems.filter((item) =>
      editableStatuses.includes(item.status),
    ).map((item) => {
      if (item.offerId) {
        return {
          id: item.offerId,
          orderItemId: item.id,
          cartItemId: `existing_${item.id}`,
          isOffer: true,
          offerId: item.offerId,
          name: item.Offer?.name || "Offer",
          price: parseFloat(item.unitPrice || 0),
          finalPrice: parseFloat(item.unitPrice || 0),
          quantity: item.quantity,
          isExistingItem: true,
        };
      }
      const cartItem = {
        id: item.menuItemId,
        orderItemId: item.id,
        cartItemId: `existing_${item.id}`,
        name: item.MenuItem?.name || "Item",
        price: parseFloat(item.unitPrice || 0),
        finalPrice: parseFloat(item.unitPrice || 0),
        quantity: item.quantity,
        image: item.MenuItem?.image,
        isExistingItem: true,
      };
      if (item.ItemVariation) {
        cartItem.selectedVariation = {
          id: item.variationId,
          name: item.ItemVariation.name,
          price: parseFloat(item.ItemVariation.price || 0),
        };
      }
      if (item.OrderItemModifiers?.length > 0) {
        cartItem.selectedModifiers = item.OrderItemModifiers.map((mod) => ({
          id: mod.modifierOptionId,
          name: mod.ModifierOption?.name || "Modifier",
          price: parseFloat(mod.ModifierOption?.price || 0),
        }));
      }
      return cartItem;
    });
  };

  // Handle checkout completion
  const handleCheckoutComplete = async (orderResult) => {
    setShowCheckoutDialog(false);
    // Auto-mark order as completed after payment is added from order details view
    try {
      await OrderServices.updateOrderStatusById(orderId, "completed");
    } catch (err) {
      console.error("Error auto-completing order after payment:", err);
    }
    if (onOrderUpdated) {
      onOrderUpdated();
    }
    setShowCompletedDialog(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Loading order details...
        </Typography>
      </Box>
    );
  }

  if (!orderDetails) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          gap: 3,
        }}
      >
        <Typography
          variant="h4"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Order not found
        </Typography>
        <Button
          variant="contained"
          onClick={onBack}
          size="large"
          sx={{
            height: 56,
            px: 4,
            borderRadius: "16px",
            fontSize: "1.1rem",
            fontWeight: 600,
          }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const statusConfig = getStatusConfig(orderDetails.status);
  const orderSummary = calculateOrderSummary();
  const createdDateTime = formatDateTime(orderDetails.createdAt);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafc",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <OrderHeader
        orderDetails={orderDetails}
        statusConfig={statusConfig}
        createdDateTime={createdDateTime}
        onBack={onBack}
        onPrint={handlePrint}
      />

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid container spacing={1.5} sx={{ flex: 1, alignItems: "stretch" }}>
          {/* Column 1 - Order Information & Payment Summary */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <OrderInfoSection orderDetails={orderDetails} />
              {orderSummary && (
                <OrderSummarySection
                  orderSummary={orderSummary}
                  formatCurrency={formatCurrency}
                  paymentStatus={orderDetails.paymentStatus}
                  onPrint={handlePrint}
                  onShare={handleShare}
                />
              )}
            </Box>
          </Grid>

          {/* Column 2 - Order Items (Compact) */}
          <Grid size={{ xs: 12, md: 6, lg: 5 }} sx={{ display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                flex: 1,
              }}
            >
              <OrderItemsSection
                orderDetails={orderDetails}
                formatCurrency={formatCurrency}
                compact
                onAddItems={
                  orderDetails?.status !== "completed" &&
                    orderDetails?.status !== "cancelled" &&
                    orderDetails?.paymentStatus !== "completed" &&
                    orderDetails?.paymentStatus !== "paid" &&
                    onEditOrder
                    ? () =>
                      onEditOrder(
                        transformPendingItemsToCart(),
                        orderDetails?.Table || null,
                        orderId,
                        orderDetails?.orderNumber,
                      )
                    : undefined
                }
              />
              {orderDetails?.status !== "completed" &&
                orderDetails?.status !== "cancelled" && (
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <OrderActionsSection
                      orderDetails={orderDetails}
                      onStatusUpdate={handleStatusUpdate}
                    />
                  </Box>
                )}
            </Box>
          </Grid>

          {/* Column 3 - Order Status Actions & New Order */}
          <Grid size={{ xs: 12, md: 12, lg: 3 }} sx={{ display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                flex: 1,
              }}
            >
              {/* Add Payment Button - Show if no payments found */}
              {!hasPayments && !checkingPayments && (
                <Button
                  variant="contained"
                  onClick={() => setShowCheckoutDialog(true)}
                  sx={{
                    flex: 1,
                    width: "100%",
                    borderRadius: "20px",
                    textTransform: "none",
                    letterSpacing: "0.3px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 2,
                    position: "relative",
                    overflow: "hidden",
                    background: `linear-gradient(135deg, #FF6600 0%, #FF8533 60%, #CC5200 100%)`,
                    color: "white",
                    boxShadow: `0 8px 28px ${alpha("#FF6600", 0.5)}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: -50,
                      right: -50,
                      width: 150,
                      height: 150,
                      borderRadius: "50%",
                      background: alpha("#fff", 0.12),
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -30,
                      left: -30,
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      background: alpha("#fff", 0.08),
                    },
                    "&:hover": {
                      background: `linear-gradient(135deg, #CC5200 0%, #FF6600 60%, #FF8533 100%)`,
                      transform: "translateY(-6px)",
                      boxShadow: `0 20px 52px ${alpha("#FF6600", 0.6)}`,
                    },
                    "&:active": { transform: "translateY(0)" },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Payment
                    sx={{ fontSize: 64, position: "relative", zIndex: 1 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.35rem",
                      fontWeight: 700,
                      position: "relative",
                      zIndex: 1,
                      lineHeight: 1.2,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Add Payment
                  </Typography>
                </Button>
              )}

              {/* New Order Button */}
              <Button
                variant="contained"
                onClick={onPOSClick || onBack}
                sx={{
                  flex: 1,
                  width: "100%",
                  borderRadius: "20px",
                  textTransform: "none",
                  letterSpacing: "0.3px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 2,
                  position: "relative",
                  overflow: "hidden",
                  background: `linear-gradient(135deg, #8b5cf6 0%, #7c3aed 60%, #6d28d9 100%)`,
                  color: "white",
                  boxShadow: `0 8px 28px ${alpha("#8b5cf6", 0.5)}`,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -50,
                    right: -50,
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    background: alpha("#fff", 0.12),
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -30,
                    left: -30,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: alpha("#fff", 0.08),
                  },
                  "&:hover": {
                    background: `linear-gradient(135deg, #6d28d9 0%, #7c3aed 60%, #8b5cf6 100%)`,
                    transform: "translateY(-6px)",
                    boxShadow: `0 20px 52px ${alpha("#8b5cf6", 0.6)}`,
                  },
                  "&:active": { transform: "translateY(0)" },
                  transition: "all 0.3s ease",
                }}
              >
                <AddCircle
                  sx={{ fontSize: 64, position: "relative", zIndex: 1 }}
                />
                <Typography
                  sx={{
                    fontSize: "1.35rem",
                    fontWeight: 700,
                    position: "relative",
                    zIndex: 1,
                    lineHeight: 1.2,
                    letterSpacing: "0.3px",
                  }}
                >
                  New Order
                </Typography>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Status Confirmation Dialog */}
      <StatusConfirmDialog
        open={confirmDialog.open}
        status={confirmDialog.status}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmStatusUpdate}
      />

      {/* Order Checkout Dialog - For adding payment to existing order */}
      {orderDetails && (
        <OrderCheckoutDialog
          open={showCheckoutDialog}
          onClose={() => setShowCheckoutDialog(false)}
          table={orderDetails.Table || null}
          cart={transformOrderToCart()}
          total={parseFloat(orderDetails.totalAmount || 0)}
          onConfirmOrder={handleCheckoutComplete}
          existingOrderId={orderId}
          existingOrderNumber={orderDetails.orderNumber}
          existingTableName={
            orderDetails.Table?.name || orderDetails.Table?.tableNumber || null
          }
          onEditOrder={(cart, table) => {
            setShowCheckoutDialog(false);
            if (onEditOrder) {
              onEditOrder(cart, table, orderId, orderDetails?.orderNumber);
            }
          }}
        />
      )}

      {/* Order Completed Dialog */}
      <Dialog
        open={showCompletedDialog}
        onClose={() => { }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            textAlign: "center",
            minWidth: 320,
            maxWidth: 400,
          },
        }}
      >
        <DialogContent sx={{ pt: 4, pb: 2, px: 4 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "#ecfdf5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <TaskAlt sx={{ fontSize: 44, color: "#10b981" }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Order Completed!
          </Typography>
          {orderDetails?.orderNumber && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Order #{orderDetails.orderNumber}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Payment has been recorded and the order is now complete.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, px: 4 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{
              bgcolor: "#10b981",
              "&:hover": { bgcolor: "#059669" },
              borderRadius: 2,
              fontWeight: 600,
              py: 1.5,
            }}
            onClick={() => {
              setShowCompletedDialog(false);
              if (onPOSClick) onPOSClick();
              else if (onBack) onBack();
            }}
          >
            Go to Dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden bill used only for printing */}
      <Box sx={{ display: "none" }}>
        <div ref={customerBillRef}>
          <CustomerBill
            orderResult={orderDetails}
            companyInfo={company}
            currency={currency || "Rs."}
          />
        </div>
      </Box>
    </Box>
  );
};

export default OrderDetailsScreen;
